import { api, handleApiError } from '../client'

// Enums
export type DeploymentStatus = 'Draft' | 'Testing' | 'Active' | 'Deprecated'
export type AgentType = 'General' | 'Writer' | 'Analyst' | 'Assistant' | 'Custom'
export type SkillCategory = 'ContentGeneration' | 'DataAnalysis' | 'Communication' | 'Research' | 'Automation' | 'Custom'

// DTOs
export interface CustomPersonalityTraitsInput {
  tone?: string
  style?: string
  expertise: string[]
  language?: string
  formalityLevel?: number
  creativityLevel?: number
  verbosityLevel?: number
}

export interface AgentSkillSummary {
  skillId: string
  name: string
  category: SkillCategory
  priority: number
  isEnabled: boolean
}

export interface AgentTestSummary {
  totalTests: number
  passedTests: number
  failedTests: number
  successRate: number
  lastTestRunAt?: string
  averageResponseTimeMs?: number
}

export interface CustomAgentDetail {
  id: string
  slug: string
  name: string
  description?: string
  agentType: AgentType
  systemPrompt: string
  personalityTraits?: CustomPersonalityTraitsInput
  primaryModel?: string
  fallbackModel?: string
  avatarUrl?: string
  isActive: boolean
  isPublic: boolean
  deploymentStatus: DeploymentStatus
  createdFromPresetId?: string
  maxResponseTokens?: number
  temperature?: number
  skills: AgentSkillSummary[]
  testResults?: AgentTestSummary
  createdAt: string
  updatedAt: string
}

export interface CreateAgentFromScratchRequest {
  slug: string
  name: string
  description?: string
  agentType: AgentType
  systemPrompt: string
  personalityTraits?: CustomPersonalityTraitsInput
  primaryModel?: string
  fallbackModel?: string
  maxResponseTokens?: number
  temperature?: number
  isPublic: boolean
  skillIds: string[]
}

export interface CreateAgentFromPresetRequest {
  presetAgentId: string
  slug: string
  name: string
  description?: string
  systemPromptOverride?: string
  personalityTraitsOverride?: CustomPersonalityTraitsInput
  primaryModelOverride?: string
  isPublic: boolean
}

export interface UpdateCustomAgentRequest {
  name?: string
  description?: string
  systemPrompt?: string
  personalityTraits?: CustomPersonalityTraitsInput
  primaryModel?: string
  fallbackModel?: string
  maxResponseTokens?: number
  temperature?: number
  isPublic?: boolean
  isActive?: boolean
}

export interface AgentValidationResult {
  isValid: boolean
  errors: ValidationIssue[]
  warnings: ValidationIssue[]
  securityFindings: SecurityFinding[]
}

export interface ValidationIssue {
  code: string
  message: string
  field?: string
}

export interface SecurityFinding {
  type: string
  severity: string
  description: string
  recommendation?: string
}

export interface AgentCostEstimate {
  model: string
  estimatedInputTokens: number
  estimatedOutputTokens: number
  costPer1000Requests: number
  monthlyEstimate: number
}

export interface AgentTestCase {
  id: string
  agentId: string
  name: string
  description?: string
  inputPrompt: string
  expectedOutput?: string
  validationCriteria?: string
  actualOutput?: string
  status: string
  lastRunAt?: string
  lastResponseTimeMs?: number
  priority: number
  category?: string
}

export interface CreateTestCaseRequest {
  agentId: string
  name: string
  description?: string
  inputPrompt: string
  expectedOutput?: string
  validationCriteria?: string
  priority?: number
  category?: string
}

export interface UpdateTestCaseRequest {
  name?: string
  description?: string
  inputPrompt?: string
  expectedOutput?: string
  validationCriteria?: string
  priority?: number
  category?: string
}

export interface AgentTestRunResult {
  agentId: string
  runAt: string
  totalTests: number
  passedTests: number
  failedTests: number
  successRate: number
  totalDurationMs: number
  results: TestCaseExecutionResult[]
}

export interface TestCaseExecutionResult {
  testCaseId: string
  name: string
  passed: boolean
  actualOutput?: string
  responseTimeMs: number
  tokensUsed: number
  errorMessage?: string
}

export interface SkillLibraryItem {
  id: string
  slug: string
  name: string
  description: string
  category: SkillCategory
  systemPromptFragment: string
  toolDefinition?: string
  inputSchema?: string
  outputSchema?: string
  defaultModel?: string
  requiresApiKey: boolean
  isBuiltIn: boolean
  usageCount: number
}

export interface PromptTemplate {
  id: string
  name: string
  description?: string
  templateText: string
  category: string
  variables: string[]
  defaultValues: Record<string, string>
  isPublic: boolean
  isBuiltIn: boolean
  usageCount: number
}

export interface CustomAgentSummary {
  id: string
  slug: string
  name: string
  description?: string
  agentType: AgentType
  isActive: boolean
  deploymentStatus: DeploymentStatus
  skillCount: number
  avatarUrl?: string
  createdAt: string
  updatedAt: string
  isCustom: boolean
}

export interface AgentStats {
  totalAgents: number
  activeAgents: number
  customAgents: number
  presetAgents: number
  totalRequests: number
  activeUsers: number
  avgResponseTimeMs: number
  agentsTrend: string
  requestsTrend: string
  usersTrend: string
  responseTimeTrend: string
}

export interface CreateSkillRequest {
  slug: string
  name: string
  description: string
  category: SkillCategory
  systemPromptFragment: string
  toolDefinition?: string
  inputSchema?: string
  outputSchema?: string
  defaultModel?: string
  requiresApiKey: boolean
  apiKeySettingName?: string
  addToLibrary?: boolean
}

export interface UpdateSkillRequest {
  name?: string
  description?: string
  category?: SkillCategory
  systemPromptFragment?: string
  toolDefinition?: string
  inputSchema?: string
  outputSchema?: string
  defaultModel?: string
  isActive?: boolean
  isInLibrary?: boolean
}

export const aiStudioService = {
  // Agent Statistics
  async getAgentStats(): Promise<AgentStats> {
    try {
      const response = await api.get<AgentStats>('/v1/studio/stats')
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Custom Agent CRUD
  async getCustomAgents(includeInactive: boolean = false, includePresets: boolean = false): Promise<CustomAgentSummary[]> {
    try {
      const params = new URLSearchParams()
      if (includeInactive) params.append('includeInactive', 'true')
      if (includePresets) params.append('includePresets', 'true')
      const queryString = params.toString() ? `?${params.toString()}` : ''
      const response = await api.get<CustomAgentSummary[]>(`/v1/studio/agents${queryString}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async createFromScratch(request: CreateAgentFromScratchRequest): Promise<CustomAgentDetail> {
    try {
      const response = await api.post<CustomAgentDetail>('/v1/studio/agents', request)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async createFromPreset(request: CreateAgentFromPresetRequest): Promise<CustomAgentDetail> {
    try {
      const response = await api.post<CustomAgentDetail>('/v1/studio/agents/from-preset', request)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async getAgent(id: string): Promise<CustomAgentDetail> {
    try {
      const response = await api.get<CustomAgentDetail>(`/v1/studio/agents/${id}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async updateAgent(id: string, request: UpdateCustomAgentRequest): Promise<CustomAgentDetail> {
    try {
      const response = await api.put<CustomAgentDetail>(`/v1/studio/agents/${id}`, request)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async deleteAgent(id: string): Promise<void> {
    try {
      await api.delete(`/v1/studio/agents/${id}`)
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Agent Lifecycle
  async validateAgent(id: string): Promise<AgentValidationResult> {
    try {
      const response = await api.post<AgentValidationResult>(`/v1/studio/agents/${id}/validate`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async estimateCost(id: string): Promise<AgentCostEstimate> {
    try {
      const response = await api.get<AgentCostEstimate>(`/v1/studio/agents/${id}/cost-estimate`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async runTests(id: string): Promise<AgentTestRunResult> {
    try {
      const response = await api.post<AgentTestRunResult>(`/v1/studio/agents/${id}/test`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async deployAgent(id: string): Promise<CustomAgentDetail> {
    try {
      const response = await api.post<CustomAgentDetail>(`/v1/studio/agents/${id}/deploy`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async deprecateAgent(id: string): Promise<CustomAgentDetail> {
    try {
      const response = await api.post<CustomAgentDetail>(`/v1/studio/agents/${id}/deprecate`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async setDeploymentStatus(id: string, status: DeploymentStatus): Promise<CustomAgentDetail> {
    try {
      const response = await api.put<CustomAgentDetail>(`/v1/studio/agents/${id}/deployment-status`, status)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Test Cases
  async getTestCases(agentId: string): Promise<AgentTestCase[]> {
    try {
      const response = await api.get<AgentTestCase[]>(`/v1/studio/agents/${agentId}/test-cases`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async addTestCase(request: CreateTestCaseRequest): Promise<AgentTestCase> {
    try {
      const response = await api.post<AgentTestCase>('/v1/studio/agents/test-cases', request)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async updateTestCase(testCaseId: string, request: UpdateTestCaseRequest): Promise<AgentTestCase> {
    try {
      const response = await api.put<AgentTestCase>(`/v1/studio/agents/test-cases/${testCaseId}`, request)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async deleteTestCase(testCaseId: string): Promise<void> {
    try {
      await api.delete(`/v1/studio/agents/test-cases/${testCaseId}`)
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async runSingleTest(testCaseId: string): Promise<TestCaseExecutionResult> {
    try {
      const response = await api.post<TestCaseExecutionResult>(`/v1/studio/agents/test-cases/${testCaseId}/run`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Skill Library
  async getSkillLibrary(category?: SkillCategory, search?: string): Promise<SkillLibraryItem[]> {
    try {
      const params = new URLSearchParams()
      if (category) params.append('category', category)
      if (search) params.append('search', search)
      const response = await api.get<SkillLibraryItem[]>(`/v1/studio/skills?${params.toString()}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async getSkill(skillId: string): Promise<SkillLibraryItem> {
    try {
      const response = await api.get<SkillLibraryItem>(`/v1/studio/skills/${skillId}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async addSkillsToAgent(agentId: string, skillIds: string[]): Promise<CustomAgentDetail> {
    try {
      const response = await api.post<CustomAgentDetail>(`/v1/studio/agents/${agentId}/skills`, skillIds)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async removeSkillFromAgent(agentId: string, skillId: string): Promise<CustomAgentDetail> {
    try {
      const response = await api.delete<CustomAgentDetail>(`/v1/studio/agents/${agentId}/skills/${skillId}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async createSkill(request: CreateSkillRequest): Promise<SkillLibraryItem> {
    try {
      const response = await api.post<SkillLibraryItem>('/v1/studio/skills', request)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async updateSkill(skillId: string, request: UpdateSkillRequest): Promise<SkillLibraryItem> {
    try {
      const response = await api.put<SkillLibraryItem>(`/v1/studio/skills/${skillId}`, request)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async deleteSkill(skillId: string): Promise<void> {
    try {
      await api.delete(`/v1/studio/skills/${skillId}`)
    } catch (error) {
      throw handleApiError(error)
    }
  },

  // Prompt Templates
  async getPromptTemplates(category?: string): Promise<PromptTemplate[]> {
    try {
      const params = category ? `?category=${category}` : ''
      const response = await api.get<PromptTemplate[]>(`/v1/studio/prompt-templates${params}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async getPromptTemplate(templateId: string): Promise<PromptTemplate> {
    try {
      const response = await api.get<PromptTemplate>(`/v1/studio/prompt-templates/${templateId}`)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },

  async applyPromptTemplate(templateId: string, variables: Record<string, string>): Promise<{ text: string }> {
    try {
      const response = await api.post<{ text: string }>(`/v1/studio/prompt-templates/${templateId}/apply`, variables)
      return response.data
    } catch (error) {
      throw handleApiError(error)
    }
  },
}
